/**
 * Friendly error rewriting for Sprocket's immediate window.
 *
 * Pyodide surfaces a *real* CPython traceback when code blows up — file paths,
 * `<exec>` frames, the whole scary wall of text. That is exactly what we do NOT
 * want a kid to face. {@link friendlyError} distills a raw traceback into a
 * small, encouraging {@link FriendlyError}: a short title, a plain-language
 * explanation of what went wrong and what to try, and the original text tucked
 * away (so a curious kid — or a grown-up helper — can still peek at "robot
 * details").
 *
 * The mapping is intentionally pure data + total: any input produces *some*
 * friendly result, so the REPL never has to special-case a parse failure.
 */

/** A kid-friendly rendering of a Python error. */
export interface FriendlyError {
  /** Short, encouraging headline (e.g. "Hmm, a typo?"). */
  title: string;
  /** Plain-language explanation + a nudge toward a fix. */
  detail: string;
  /** The Python exception class name we matched (or "Error" if unknown). */
  kind: string;
  /** The original traceback text, kept for the collapsible "robot details". */
  raw: string;
}

/** Signature of a single error rewriter: given the message tail, explain it. */
type Rewriter = (message: string) => { title: string; detail: string };

/**
 * Per-exception rewriters. Each receives the *message* portion (the text after
 * `ExceptionType: `) so it can personalise the explanation (e.g. quote the
 * undefined name). Keep the language warm, concrete, and free of jargon.
 */
const REWRITERS: Record<string, Rewriter> = {
  SyntaxError: () => ({
    title: "Python got confused 🤔",
    detail:
      "That line didn't look like Python. Check for a missing quote, bracket, " +
      "or colon — and make sure your quotes come in pairs.",
  }),
  IndentationError: () => ({
    title: "Watch the spaces 📏",
    detail:
      "Python cares about the spaces at the start of a line. Lines inside a " +
      "loop or an if need to be lined up the same way.",
  }),
  TabError: () => ({
    title: "Watch the spaces 📏",
    detail:
      "Try not to mix tabs and spaces at the start of a line — pick spaces " +
      "and Python will be happy.",
  }),
  NameError: (message) => {
    const name = /name '([^']+)'/.exec(message)?.[1];
    return {
      title: "What's that name? 🏷️",
      detail: name
        ? `Python doesn't know what "${name}" is yet. Check the spelling, or ` +
          `make it first — like ${name} = 5.`
        : "Python ran into a name it doesn't know yet. Check the spelling, or " +
          "make it first with an = sign.",
    };
  },
  TypeError: () => ({
    title: "Those don't fit together 🧩",
    detail:
      "You asked Python to mix two things that don't go together — like adding " +
      "a word to a number. Try making them the same kind first.",
  }),
  ValueError: () => ({
    title: "That value won't work 🙅",
    detail:
      "Python understood the command but didn't like the value it got. Try a " +
      "different value — for example, int(\"5\") works but int(\"five\") doesn't.",
  }),
  ZeroDivisionError: () => ({
    title: "Can't divide by zero ➗",
    detail:
      "Splitting something into zero pieces doesn't make sense — even to a " +
      "robot! Try dividing by a number other than 0.",
  }),
  IndexError: () => ({
    title: "That spot is empty 🔢",
    detail:
      "You asked for an item that isn't there. Lists start counting at 0, so " +
      "the first item is [0]. Check how many items you actually have.",
  }),
  KeyError: (message) => {
    const key = message.trim().replace(/^['"]|['"]$/g, "");
    return {
      title: "Can't find that key 🔑",
      detail: key
        ? `There's no "${key}" inside that box. Check the spelling, or add it ` +
          "first."
        : "That key isn't inside the box yet. Check the spelling, or add it " +
          "first.",
    };
  },
  AttributeError: () => ({
    title: "That trick isn't there 🪄",
    detail:
      "You asked something to do a trick it doesn't know. Check the spelling " +
      "of the part after the dot.",
  }),
  ModuleNotFoundError: (message) => {
    const mod = /'([^']+)'/.exec(message)?.[1];
    return {
      title: "Can't find that toolbox 🧰",
      detail: mod
        ? `Python couldn't find a toolbox called "${mod}". Check the spelling.`
        : "Python couldn't find that toolbox. Check the spelling.",
    };
  },
  ImportError: () => ({
    title: "Can't find that toolbox 🧰",
    detail: "Python couldn't bring in that tool. Check the spelling.",
  }),
  RecursionError: () => ({
    title: "Round and round forever 🌀",
    detail:
      "Your code kept calling itself and never stopped. Make sure there's a " +
      "way for it to finish.",
  }),
  KeyboardInterrupt: () => ({
    title: "Stopped 🛑",
    detail: "That run was stopped before it finished.",
  }),
};

/**
 * The last meaningful line of a CPython traceback is the exception itself,
 * shaped like `ExceptionType: message`. Pull it out, ignoring trailing blank
 * lines. Returns the whole trimmed text if no such line exists.
 */
function lastErrorLine(raw: string): string {
  const lines = raw
    .split("\n")
    .map((l) => l.trimEnd())
    .filter((l) => l.length > 0);
  return lines.length > 0 ? lines[lines.length - 1] : raw.trim();
}

/**
 * Turn a raw traceback (or any error string) into a {@link FriendlyError}.
 *
 * Total by construction: unrecognised errors fall back to a generic, still
 * encouraging message. The exception class is matched from the final
 * `Type: message` line of the traceback.
 */
export function friendlyError(raw: string): FriendlyError {
  const text = raw ?? "";
  const line = lastErrorLine(text);

  // Match "SomeError: the message" or a bare "SomeError".
  const match = /^([A-Za-z_][A-Za-z0-9_]*)\s*:?\s*(.*)$/.exec(line);
  const kind = match?.[1] ?? "Error";
  const message = match?.[2] ?? "";

  const rewriter = REWRITERS[kind];
  if (rewriter) {
    const { title, detail } = rewriter(message);
    return { title, detail, kind, raw: text };
  }

  // Unknown error: stay warm and unscary, but still honest.
  return {
    title: "Something went wrong 🤖",
    detail:
      "The robot hit a snag running that. Take a look at your code and try " +
      "again — you've got this!",
    kind: kind.endsWith("Error") || kind.endsWith("Exception") ? kind : "Error",
    raw: text,
  };
}
